import { getInitialResults, waitForResults } from "instantsearch.js/es/lib/server.js";
import { walkIndex } from "instantsearch.js/es/lib/utils/index.js";
import React from 'react';
import { InstantSearchServerContext, InstantSearchSSRProvider } from 'react-instantsearch-hooks';
/**
 * Returns the InstantSearch server state from a component.
 */
export function getServerState(children) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var searchRef = {
    current: undefined
  };
  var createNotifyServer = function createNotifyServer() {
    var hasBeenNotified = false;
    var notifyServer = function notifyServer(_ref) {
      var search = _ref.search;
      if (hasBeenNotified) {
        throw new Error('getServerState should be called with a single InstantSearchSSRProvider and a single InstantSearch component.');
      }
      hasBeenNotified = true;
      searchRef.current = search;
    };
    return notifyServer;
  };
  return importRenderToString(options.renderToString).then(function (renderToString) {
    return execute({
      children: children,
      renderToString: renderToString,
      searchRef: searchRef,
      notifyServer: createNotifyServer()
    }).then(function (serverState) {
      return {
        serverState: serverState,
        renderToString: renderToString
      };
    });
  }).then(function (_ref2) {
    var renderToString = _ref2.renderToString,
      serverState = _ref2.serverState;
    var shouldRefetch = false;

    // <DynamicWidgets> requires another query to retrieve the dynamic widgets
    // to render.
    walkIndex(searchRef.current.mainIndex, function (index) {
      shouldRefetch = shouldRefetch || index.getWidgets().some(function (widget) {
        return widget.$$type === 'ais.dynamicWidgets';
      });
    });
    if (shouldRefetch) {
      return execute({
        children: /*#__PURE__*/React.createElement(InstantSearchSSRProvider, serverState, children),
        renderToString: renderToString,
        searchRef: searchRef,
        notifyServer: createNotifyServer()
      });
    }
    return serverState;
  });
}
function execute(_ref3) {
  var children = _ref3.children,
    renderToString = _ref3.renderToString,
    notifyServer = _ref3.notifyServer,
    searchRef = _ref3.searchRef;
  return Promise.resolve().then(function () {
    renderToString( /*#__PURE__*/React.createElement(InstantSearchServerContext.Provider, {
      value: {
        notifyServer: notifyServer
      }
    }, children));
  }).then(function () {
    return (
      // We wait for the component to mount so that `notifyServer()` is called.
      new Promise(function (resolve) {
        return setTimeout(resolve, 0);
      })
    );
  }).then(function () {
    // If `notifyServer()` is not called by then, it means that <InstantSearch>
    // wasn't within the `children`.
    // We decide to go with a strict behavior in that case; throwing. If users have
    // some routes that don't mount the <InstantSearch> component, they would need
    // to try/catch the `getServerState()` call.
    // If this behavior turns out to be too strict for many users, we can decide
    // to warn instead of throwing.
    if (!searchRef.current) {
      throw new Error("Unable to retrieve InstantSearch's server state in `getServerState()`. Did you mount the <InstantSearch> component?");
    }
    return waitForResults(searchRef.current);
  }).then(function () {
    return {
      initialResults: getInitialResults(searchRef.current.mainIndex)
    };
  });
}
function importRenderToString(renderToString) {
  if (renderToString) {
    return Promise.resolve(renderToString);
  }
  // eslint-disable-next-line no-console
  console.warn('[InstantSearch] `renderToString` should be passed to getServerState(<App/>, { renderToString })');

  // React pre-18 doesn't use `exports` in package.json, requiring a fully resolved path
  // Thus, only one of these imports is correct
  var modules = ['react-dom/server.js', 'react-dom/server'];

  // import is an expression to make sure https://github.com/webpack/webpack/issues/13865 does not kick in
  return Promise.all(modules.map(function (mod) {
    return import(mod).catch(function () {});
  })).then(function (imports) {
    var ReactDOMServer = imports.find(function (mod) {
      return mod !== undefined;
    });
    if (!ReactDOMServer) {
      throw new Error('Could not import ReactDOMServer. You can provide it as an argument: getServerState(<Search />, { renderToString }).');
    }
    return ReactDOMServer.renderToString;
  });
}